import React, { useState, useMemo } from 'react';
import { AxiosInstance } from 'axios';
import { useCurrentUser } from '../../../../hooks';
import { DataContext } from '../context';
import type { DataProvider } from '../types';
import { useInfiniteQuery, useQuery } from 'react-query';
import { SearchResource } from '../../../../types/domains/Search';
import {
  V2QriResolverResponseSchema,
  V2ResponseSchema,
  V2Result,
} from './types';
import { useDebounce } from '../../../../hooks/useDebounce';

export type QueryClauseType = Record<string, string | number | boolean>;
type QueryClauseWithMultiplerTerms = Record<string, Array<string | number>>;

export type QuickSearchQueryClause = {
  match?: QueryClauseType;
  term?: QueryClauseType;
  terms?: QueryClauseWithMultiplerTerms;
};

export type QuickSearchV2DataProviderProps<T extends SearchResource> = {
  /**
   * QuickSearch API client. Used to fetch search results.
   */
  quickSearchClient: AxiosInstance;
  /**
   * QRI Service api client. Used to resolve previously selected QRIs.
   */
  qriClient: AxiosInstance;
  /**
   * The Qualio resource type to search for, must be a valid SearchResource or an array of valid SearchResource.
   */
  resource: T | T[];
  /**
   * Optional resource sub type to limit search to.
   */
  resourceSubType?: string;
  /**
   * Raw opensearch MUST clause
   * see https://opensearch.org/docs/latest/query-dsl/compound/bool/
   */
  must?: QuickSearchQueryClause[];
  /**
   * Raw opensearch SHOULD clause
   * see https://opensearch.org/docs/latest/query-dsl/compound/bool/
   */
  should?: readonly QuickSearchQueryClause[];
  /**
   * List of QRIs to pre-populate the results in addition to the query
   */
  defaultDataQRIs?: readonly string[];
  children: React.ReactNode;
};

const PAGE_SIZE = 10;
const WILDCARD_TERM = '*';

export const QuickSearchV2 = <T extends SearchResource>({
  quickSearchClient,
  qriClient,
  resource,
  resourceSubType,
  must,
  should,
  defaultDataQRIs,
  children,
}: QuickSearchV2DataProviderProps<T>): React.ReactElement => {
  const { companyId } = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [mustQuery, setMustQuery] = useState<
    QuickSearchQueryClause[] | undefined
  >(must);

  const [resourceFilterSelected, setFilterResourceSelected] = useState(false);

  const { capabilities } = useCurrentUser();

  const filteredResources = useMemo(() => {
    if (!resource) {
      return [];
    }

    let result: SearchResource[] = Array.isArray(resource)
      ? [...resource]
      : [resource];

    if (!capabilities?.design_controls) {
      result = result.filter(
        (r) =>
          ![
            'dc-requirement',
            'dc-testCase',
            'dc-change-control',
            'dc-risk',
            'design-controls-configuration',
          ].includes(r),
      );
    }

    if (!capabilities?.events) {
      result = result.filter(
        (r) =>
          !['quality-event', 'event-template', 'quality-event-task'].includes(
            r,
          ),
      );
    }

    if (!capabilities?.suppliers) {
      result = result.filter(
        (r) => !['supplier', 'supplier-configuration'].includes(r),
      );
    }

    if (!capabilities?.compliance) {
      result = result.filter((r) => !['compliance-control-task'].includes(r));
    }

    return result;
  }, [
    resource,
    capabilities?.design_controls,
    capabilities?.events,
    capabilities?.suppliers,
    capabilities?.compliance,
  ]);

  const [resources, setResources] = useState<SearchResource | SearchResource[]>(
    filteredResources,
  );

  const qriResult = useQuery(['QriDetails', defaultDataQRIs], async () => {
    if (!defaultDataQRIs || defaultDataQRIs.length === 0) {
      return undefined;
    }
    const response = await qriClient.post('/details', {
      qris: defaultDataQRIs,
    });

    return Object.values(V2QriResolverResponseSchema.parse(response.data));
  });

  const debouncedSearchTerm = useDebounce(searchTerm || WILDCARD_TERM, {
    delay: 500,
    enabled: !!searchTerm,
  });

  const { data, ...result } = useInfiniteQuery({
    queryKey: [
      'QuickSearchV2',
      resources,
      companyId,
      debouncedSearchTerm,
      should,
      mustQuery,
      PAGE_SIZE,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await quickSearchClient.get(`/v2/quicksearch`, {
        params: {
          companyId,
          term: debouncedSearchTerm,
          resources: Array.isArray(resources) ? resources.join(',') : resources,
          resourceSubType,
          should: should ? JSON.stringify(should) : undefined,
          must:
            mustQuery && mustQuery?.length !== 0
              ? JSON.stringify(mustQuery)
              : undefined,
          size: PAGE_SIZE,
          page: pageParam,
        },
      });
      return V2ResponseSchema.parse(response.data);
    },
    getNextPageParam: (currentPage) => {
      return currentPage.results.length > 0 && currentPage.total > PAGE_SIZE
        ? currentPage.page + 1
        : undefined;
    },
  });

  const allData = useMemo(() => {
    const qriResults = qriResult.data ?? [];
    // Merge QRI results with search results, avoiding duplicates.
    return [
      ...qriResults,
      ...(data?.pages ?? [])
        .flatMap((page) => page.results)
        .filter((r) => !qriResults.find((q) => q.id === r.id)),
    ];
  }, [data, qriResult]);

  return (
    <DataContext.Provider
      value={
        {
          ...result,
          data: allData,
          onSearchTermChange: setSearchTerm,
          hasNextPage: result.hasNextPage || false,
          isLoading: qriResult.isLoading || result.isLoading,
          error: qriResult.error ?? result.error,
          search: {
            client: quickSearchClient,
            defaultResources: filteredResources,
            mustQuery,
            resource: resources,
            resourceFilterSelected,
            onMustQueryChange: setMustQuery,
            onResourcesChange: setResources,
            onResourceFilterSelectedChange: setFilterResourceSelected,
          },
        } satisfies DataProvider<V2Result>
      }
    >
      {children}
    </DataContext.Provider>
  );
};
